import { useEffect, useState } from 'react';
import { ApiError } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import { createAccount } from './api';
import { effectivePerms, scopeBrief, summarizeRole } from './permissions';
import type { Role, UserRow } from './types';
import s from './AccountFormSheet.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

function generateTemporaryPassword() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-';
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
}

export function AccountCreateSheet({ roles, canCreate, onClose, onCreated }: {
  roles: Role[];
  canCreate: boolean;
  onClose: () => void;
  onCreated: (account: UserRow) => void;
}) {
  const ff = useFormFactor();
  const activeRoles = roles.filter((role) => role.isActive);
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [roleCode, setRoleCode] = useState(() => activeRoles.find((role) => role.code === 'FOLLOWER')?.code || activeRoles[0]?.code || '');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [createdAccount, setCreatedAccount] = useState<UserRow | null>(null);
  const [passwordMode, setPasswordMode] = useState<'temporary' | 'link'>('temporary');
  const [setupExpiry, setSetupExpiry] = useState('');
  const [oneTimePassword, setOneTimePassword] = useState('');
  const [copyError, setCopyError] = useState('');
  useEffect(() => {
    if (!activeRoles.length) return;
    setRoleCode((current) => activeRoles.some((role) => role.code === current) ? current : activeRoles.find((role) => role.code === 'FOLLOWER')?.code || activeRoles[0].code);
  }, [roles]);
  const role = activeRoles.find((item) => item.code === roleCode);
  const summary = role ? summarizeRole(role) : null;
  const scope = role ? scopeBrief(role, effectivePerms(role)) : null;
  const scopeText = scope?.label === '分配给我' ? '查看分配给自己的订单' : scope?.label === '本人创建' ? '查看本人创建的订单' : scope?.label === '全部订单' ? '查看全部订单' : scope?.label === '不看订单' ? '' : `查看${scope?.label}的订单`;
  const actionText = summary?.headline.replace(/[。.]$/, '') || '';
  const hiddenText = summary?.hidden.length ? `看不到${summary.hidden.join('、')}` : summary?.cannot.slice(0, 1).join('、') || '';
  const valid = /^[a-zA-Z0-9_.-]{3,32}$/.test(username) && !!name.trim() && !!role;
  const submit = async () => {
    if (!valid || !canCreate || READONLY || saving || uncertain) return;
    const temporaryPassword = generateTemporaryPassword();
    setSaving(true); setError('');
    try {
      const created = await createAccount({ username, name: name.trim(), role: roleCode, password: temporaryPassword, passwordMode, isActive: active });
      setCreatedAccount(created);
      setOneTimePassword(passwordMode === 'link' ? `${window.location.origin}${import.meta.env.BASE_URL}password-setup#${created.setupToken}` : temporaryPassword);
      setSetupExpiry(created.setupExpiresAt || '');
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '创建账号失败');
    } finally { setSaving(false); }
  };
  const finish = () => {
    if (createdAccount) {
      const account = createdAccount;
      setOneTimePassword('');
      onCreated(account);
    }
  };
  const close = () => {
    if (!saving) {
      setOneTimePassword('');
      onClose();
    }
  };
  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
    <section className={`${s.sheet} ${s.createSheet}`} role="dialog" aria-modal="true" aria-label={createdAccount ? passwordMode === 'link' ? '设置密码链接' : '临时密码' : '新建账号'}>
      {createdAccount ? <>
        <header className={`${s.head} ${s.createHead}`}><h2>{passwordMode === 'link' ? '设置密码链接' : '临时密码'}</h2><button type="button" onClick={finish} aria-label="关闭">×</button></header>
        <div className={`${s.body} ${s.createBody} ${s.createResult}`}>
          <h3>已创建账号 {createdAccount.name}</h3>
          <p>{passwordMode === 'link' ? `把链接私下交给 ${createdAccount.name}，由本人设置密码。24 小时内有效，只能使用一次。` : `把下面的临时密码安全地交给 ${createdAccount.name}。请对方首次登录后改成自己的密码。`}</p>
          <output className={passwordMode === 'link' ? s.setupLink : undefined}>{oneTimePassword}</output>{setupExpiry && <p>有效期至 {new Date(setupExpiry).toLocaleString()}</p>}
          <button type="button" className={s.copyPassword} onClick={() => { void navigator.clipboard.writeText(oneTimePassword).then(() => setCopyError('')).catch(() => setCopyError('复制失败，请手动选中并复制')); }}>复制</button>
          {copyError && <p className={s.error} role="alert">{copyError}</p>}
          <p className={s.createCaution}>只显示这一次。离开此页后无法再次查看；请当面交给对方或私聊发送，不要发到群里。</p>
        </div>
        <footer className={`${s.foot} ${s.createFoot}`}><button type="button" className={s.primary} onClick={finish}>完成</button></footer>
      </> : <>
        <header className={`${s.head} ${s.createHead} ${ff === 'phone' ? s.createPhoneHead : ''}`}>
          {ff === 'phone' ? <button type="button" onClick={close} disabled={saving}>取消</button> : <h2>新建账号</h2>}
          {ff === 'phone' && <h2>新建账号</h2>}
          {ff === 'phone' ? <span aria-hidden="true" /> : <button type="button" onClick={close} disabled={saving} aria-label="关闭">×</button>}
        </header>
        <div className={`${s.body} ${s.createBody}`}>
          <div className={`${s.grid} ${s.createGrid}`}>
            <label><span><i>*</i> 登录账号</span><input autoFocus value={username} maxLength={32} autoCapitalize="off" autoComplete="off" placeholder="zhaohong" onChange={(event) => setUsername(event.target.value)} /></label>
            <label><span><i>*</i> 姓名</span><input value={name} maxLength={80} placeholder="赵红" onChange={(event) => setName(event.target.value)} /></label>
            <label className={s.wide}><span><i>*</i> 角色</span><select value={roleCode} onChange={(event) => setRoleCode(event.target.value)}>{activeRoles.map((item) => <option key={item.id} value={item.code}>{item.name}</option>)}</select>{role && scope && <small>{effectivePerms(role).size} 项权限 · 数据范围「{scope.label}」</small>}</label>
          </div>
          <section className={s.passwordChoices} aria-labelledby="initial-password-title">
            <div className={s.passwordChoiceTitle}><b id="initial-password-title">* 初始密码</b><span>不用你输入</span></div>
            <button type="button" className={passwordMode === 'temporary' ? s.passwordChoiceSelected : s.passwordChoiceDisabled} aria-pressed={passwordMode === 'temporary'} onClick={() => setPasswordMode('temporary')}>
              <span className={s.passwordChoiceIcon} aria-hidden="true">✦</span>
              <span><b>生成临时密码</b><small>只显示一次，建议首次登录修改</small></span>
              <span className={s.choiceRadio} aria-hidden="true" />
            </button>
            <button type="button" className={passwordMode === 'link' ? s.passwordChoiceSelected : s.passwordChoiceDisabled} aria-pressed={passwordMode === 'link'} onClick={() => setPasswordMode('link')}>
              <span className={s.passwordChoiceIcon} aria-hidden="true">↗</span>
              <span><b>生成设置链接</b><small>本人打开后自己设，24 小时内有效</small></span>
              <span className={s.choiceRadio} aria-hidden="true" />
            </button>
            <p className={s.passwordPrivacy}>系统随机生成（至少 8 位）。手机上不替别人输入密码，减少明文暴露。</p>
          </section>
          <label className={`${s.toggle} ${s.createToggle} ${s.createStatus}`}><span>启用</span><span><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /><small>停用的账号不能登录</small></span></label>
          {role && summary && <div className={`${s.createSummary}`}><section className={s.createCan}><h3><span aria-hidden="true">✓</span> 这个账号将能做什么</h3><p>{[scopeText, actionText].filter(Boolean).join('，')}。</p></section>{hiddenText && <section className={s.createCannot}><p>{hiddenText}。</p></section>}</div>}
          {!activeRoles.length && <p className={s.error}>没有可分配的启用角色。</p>}
          {!canCreate && <p className={s.note}>当前账号没有新建账号权限。</p>}
          {READONLY && <p className={s.note}>当前为生产只读模式，不能提交。</p>}
          {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请先核对账号列表，暂勿重复提交。'}</p>}
        </div>
        <footer className={`${s.foot} ${s.createFoot}`}>
          {ff !== 'phone' && <button type="button" onClick={close} disabled={saving}>取消</button>}
          <button type="button" className={s.primary} disabled={!valid || !canCreate || READONLY || saving || uncertain} onClick={() => void submit()}>{saving ? '创建中…' : '创建账号'}</button>
        </footer>
      </>}
    </section>
  </div>;
}
